/**
 * TrendChart — the metric trend with the trust envelope drawn on the line (A2 tier 1):
 * a neutral Actual line whose per-point dot carries the server RAG (green beats target,
 * red misses, grey no-target/NULL) with an amber ring when the locked value diverges;
 * a dashed Target threshold line; a shaded band over the locked periods; honest null
 * gaps (connectNulls=false — no line drawn through missing months). Recharts (pinned
 * 2.15.4). Typed props only (CD-7); every colour comes from server truth, not re-derived.
 */

import React from 'react'
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import type { MetricFormat } from '../../types/metrics'
import { periodHeading } from './cellFormat'
import { ragHex, TREND_HEX } from './trendColors'
import type { TrendPoint, TrendSeries } from './trendSeries'
import TrendTooltip from './TrendTooltip'
import { forecastKey, measuredKey, splitSeries } from './forecastSplit'

interface ActualDotArgs {
  cx?: number
  cy?: number
  index?: number
  payload?: TrendPoint
}

function yAxisLabel(value: number, format: MetricFormat): string {
  if (format === 'gbp_k') return `£${value}k`
  if (format === 'pct') return `${value}%`
  return `${value}`
}

/** Per-point Actual dot: fill = server RAG; a ring marks the lock state (amber when the
 * value diverges from production, slate when merely locked) — so a lone locked month still
 * reads as locked even when the period band collapses to zero width. */
function renderActualDot(props: ActualDotArgs): React.ReactElement {
  const { cx, cy, index, payload } = props
  const key = payload?.period ?? `dot-${index}` // Recharts renders dots as a list → keyed
  const cell = payload?.cell ?? null
  if (cx == null || cy == null || !cell) return <circle key={key} r={0} />
  const ring =
    cell.lock_state === 'divergent'
      ? TREND_HEX.divergent
      : cell.lock_state === 'locked'
        ? TREND_HEX.lockedRing
        : null
  return (
    <g key={key}>
      {ring && <circle cx={cx} cy={cy} r={6.5} fill="none" stroke={ring} strokeWidth={2} />}
      <circle cx={cx} cy={cy} r={4} fill={ragHex(cell)} stroke={TREND_HEX.surface} strokeWidth={2} />
    </g>
  )
}

/** Chart rows from the series' points: every TrendPoint field the tooltip reads is kept
 *  (it reads `cell` off the row), plus the measured/forecast keys the two lines draw. */
function splitPoints(points: TrendSeries['points']): Array<Record<string, unknown>> {
  const rows = points.map((point) => ({ ...point })) as unknown as Array<
    Record<string, string | number | null>
  >
  splitSeries(
    rows,
    'actual',
    points.map((point) => point.actual),
    points.map((point) => point.forecast)
  )
  return rows as unknown as Array<Record<string, unknown>>
}

function targetLine(): React.ReactElement {
  return (
    <Line
      name="Target"
      type="monotone"
      dataKey="target"
      stroke={TREND_HEX.target}
      strokeWidth={1.5}
      strokeDasharray="4 4"
      dot={false}
      connectNulls={false}
      isAnimationActive={false}
    />
  )
}

function actualLine(): React.ReactElement {
  return (
    <Line
      name="Actual"
      type="monotone"
      dataKey={measuredKey('actual')}
      stroke={TREND_HEX.actual}
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      dot={renderActualDot}
      activeDot={false}
      connectNulls={false}
      isAnimationActive={false}
    />
  )
}

/** The forward run, dashed and joined to the solid line at the last complete month. No RAG
 *  dots on it: an open period gets no verdict, so there is none to draw. */
function forecastLine(): React.ReactElement {
  return (
    <Line
      name="Forecast"
      type="monotone"
      dataKey={forecastKey('actual')}
      stroke={TREND_HEX.actual}
      strokeWidth={2}
      strokeDasharray="5 4"
      dot={false}
      activeDot={false}
      connectNulls={false}
      isAnimationActive={false}
    />
  )
}

/** A wash of the Actual hue under the line — body at a glance, never a solid block.
 *  Drawn before the line so the 2px stroke stays the crisp edge of the shape. Bound to the
 *  MEASURED key only: a filled body under a forecast would give an estimate the visual
 *  weight of a record. */
function actualArea(): React.ReactElement {
  return (
    <Area
      type="monotone"
      dataKey={measuredKey('actual')}
      legendType="none"
      tooltipType="none"
      stroke="none"
      fill={TREND_HEX.actual}
      fillOpacity={0.1}
      connectNulls={false}
      isAnimationActive={false}
    />
  )
}

export default function TrendChart({
  series,
  compact = false,
}: {
  series: TrendSeries
  compact?: boolean
}): React.JSX.Element {
  const rows = React.useMemo(() => splitPoints(series.points), [series.points])
  return (
    <ResponsiveContainer width="100%" height={compact ? 190 : 280}>
      <ComposedChart data={rows} margin={{ top: 10, right: 14, bottom: 4, left: 4 }}>
        <CartesianGrid stroke={TREND_HEX.grid} vertical={false} />
        {series.lockedFrom && (
          <ReferenceArea x1={series.lockedFrom} x2={series.lockedTo ?? series.lockedFrom} fill={TREND_HEX.lockedShade} />
        )}
        <XAxis
          dataKey="period" tickFormatter={periodHeading} interval="preserveStartEnd" minTickGap={16}
          tick={{ fontSize: 10, fill: 'var(--mut2)' }} tickLine={false} axisLine={{ stroke: 'var(--line)' }}
        />
        <YAxis
          tickFormatter={(value: number) => yAxisLabel(value, series.format)} width={52}
          tick={{ fontSize: 10, fill: 'var(--mut2)' }} tickLine={false} axisLine={false}
        />
        <Tooltip content={<TrendTooltip />} cursor={{ stroke: 'var(--line)', strokeWidth: 1 }} />
        {!compact && <Legend wrapperStyle={{ fontSize: 11, color: 'var(--mut)' }} iconSize={9} />}
        {actualArea()}
        {targetLine()}
        {actualLine()}
        {forecastLine()}
      </ComposedChart>
    </ResponsiveContainer>
  )
}
